import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import Notification from "../../components/UI/Notification";
import { getToken } from "../../../src/utils/tokenUtils";
import Drawer from "../../components/UI/Drawer";
import ReactPaginate from 'react-paginate';
import { CheckCircle, XCircle, User, Calendar, Briefcase, Clock, X, MoreVertical, Eye, Check, MinusCircle } from 'lucide-react';
import { Link } from "react-router-dom";
import { getRoleBasedRoute } from "../../utils/routeUtils";
import { AuthContext } from "../../contexts/AuthContext";
import StatusBadge from '../../components/UI/StatusBadge';


const LeaveApproverPage = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [notification, setNotification] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filter, setFilter] = useState("All");
  const token = getToken();
  const [pagination, setPagination] = useState({
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 15,
      });
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [total_days, setTotalDays] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  
  // filter
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [filteredData, setFilteredData] = useState(leaveRequests);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState(""); // "approved" or "rejected"
  const [approverComments, setApproverComments] = useState("");

  const { roles } = useContext(AuthContext);
  const getRoleBasedRouteLocal = (route) => getRoleBasedRoute(route, roles);
  
  const handleFilterChange = (field, value) => {
    setActiveFilters(prev => ({
    ...prev,
    [field]: value
    }));
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery("");
  };


  useEffect(() => {
    fetchLeaveRequests();
  }, [token]);

  useEffect(() => {
      // Apply both search query and filters
      let result = leaveRequests;

      // Apply search query filtering
      if (searchQuery) {
      const lowercasedQuery = searchQuery.toLowerCase();
      result = result.filter((item) => {
          // Search across all relevant fields
          return (
          (item.user.name?.toLowerCase().includes(lowercasedQuery)) ||
          (item.request_date?.toLowerCase().includes(lowercasedQuery)) ||
          (item.requestable_type?.toString().toLowerCase().includes(lowercasedQuery)) ||
          (item.status?.toLowerCase().includes(lowercasedQuery))
          );
      });
      }

      // Apply column-specific filters
      Object.entries(activeFilters).forEach(([field, filterValue]) => {
      if (filterValue) {
          result = result.filter(item => {
          const value = item[field];
          if (field === 'created_at' && value) {
              // Date filtering
              const itemDate = new Date(value);
              const day = String(itemDate.getDate()).padStart(2, '0');
              const month = String(itemDate.getMonth() + 1).padStart(2, '0');
              const year = itemDate.getFullYear();
              const formattedDate = `${day}-${month}-${year}`;
              
              return formattedDate.includes(filterValue);
          } else {
              // Standard string filtering
              return value?.toString().toLowerCase().includes(filterValue.toLowerCase());
          }
          });
      }
      });

      const filteringNow = searchQuery.length > 0 || Object.values(activeFilters).some(v => !!v);
      setIsFiltering(filteringNow);
      setFilteredData(result);
  }, [searchQuery, activeFilters, leaveRequests]);

  const fetchLeaveRequests = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `https://hrmspayroll.prepstripe.com/api/approvals?page=${page}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
  
      const data = response.data; // Ensure correct data structure
      setLeaveRequests(data?.data || []);
      setLoading(false);
      setPagination({
        currentPage: data?.current_page || 1,
        totalPages: data?.last_page || 1,
        totalItems: data?.total || 0,
        perPage: data?.per_page || 10,
      });
  
    } catch (error) {
      console.error("Error fetching leave requests", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleApproveReject = async (id, status) => {
    try {
      const response = await axios.put(
        `https://hrmspayroll.prepstripe.com/api/approvals/${id}`,
        { status, approver_comments: approverComments, },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      fetchLeaveRequests();
      setIsDetailModalOpen(false);
      setOpenDropdownId(null);
      // setNotification("Request status updated successfully");
      setNotification({
        message: "Request status updated successfully",
        type: "success",
      });
    } catch (error) {
      console.error("Error updating status", error);

      const errorMsg = error?.response?.data?.error || error.message || "An error occurred";
      setNotification(errorMsg);
    }
  };
  

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchLeaveRequests(newPage);
    }
  };

  // console.log("Total Pages", pagination?.totalPages)

  const renderPageNumbers = () => {
      const pages = [];
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(
          <button
            key={i}
            onClick={() => handlePageChange(i)}
            className={`px-4 py-2 rounded-md mx-1 ${
              currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
          >
            {i}
          </button>
        );
      }
      return pages;
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    const filtering = value !== '';
    setIsFiltering(filtering);
  
    if (!filtering) {
      // Search cleared, restore current page's data
      handlePageChange(currentPage); // fetchResponses will be called inside
    }
  };
  

  // Filtered Leaves
  const filteredRequests = leaveRequests
  .filter((leave) => {
    if (filter === "All") return true;
    return leave.status.toLowerCase() === filter.toLowerCase();
  })
  .filter((leave) => {
    if (!searchTerm) return true;

    const lowerSearch = searchTerm.toLowerCase();

    return (
      (leave.user.name && leave.user.name.toLowerCase().includes(lowerSearch)) ||
      (leave.requestable_type && leave.requestable_type.toLowerCase().includes(lowerSearch)) ||
      (leave.status && leave.status.toLowerCase().includes(lowerSearch)) ||
      (leave.request_date && leave.request_date.toLowerCase().includes(lowerSearch))
    );
  });


  return (
    <div className="px-4 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">
      
      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Breadcrumb */}
      <nav className="flex flex-wrap mb-4" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-2 rtl:space-x-reverse">
          <li>
            <div className="flex items-center">
              <Link
                to={getRoleBasedRouteLocal("/admin/attendance_management")}
                className="text-sm sm:text-md font-medium text-black hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500"
              >
                Attendance Management
              </Link>
            </div>
          </li>
          <li aria-current="page">
            <div className="flex items-center">
              <svg className="w-3 h-3 text-gray-400 mx-1" aria-hidden="true" fill="none" viewBox="0 0 6 10">
                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
              </svg>
              <span className="ms-1 text-sm sm:text-md font-medium text-gray-500 md:ms-2 dark:text-gray-400">Leave Requests</span>
            </div>
          </li>
        </ol>
      </nav>

      <h2 className="text-lg sm:text-xl dark:text-white font-semibold mb-4">Leave Requests</h2>

      {/* Search and filter controls */}
      <div className="flex flex-col sm:flex-row gap-4 justify-start items-center mb-6">
        {/* search field */}
        <div className="relative w-full sm:w-80">
          <input
              type="text"
              placeholder="Search by name, type, or date..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full dark:bg-black dark:text-white dark:border-gray-700 placeholder:text-xs md:placeholder:text-sm p-2.5 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* direct status filter */}
        <div className="w-full sm:w-48">
          <select
            value={activeFilters.status || ""}
            onChange={(e) => handleFilterChange("status", e.target.value)}
            className="w-full dark:bg-black dark:text-white dark:border-gray-700 p-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
          >
            <option value="">All Status</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        {isFiltering && (
          <button
            onClick={clearFilters}
            className="text-sm text-red-500 hover:text-red-700 font-medium hover:underline transition-colors"
          >
            Clear All
          </button>
        )}
      </div>


      <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg overflow-visible">
        <table className="min-w-full table-auto md:table-fixed text-sm">
          <thead className="bg-gray-100 dark:bg-gray-800 dark:text-white">
            <tr className="text-sm sm:text-base">
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">User</th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Date</th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Request Type</th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Status</th>
              <th className="py-2 px-4 dark:text-white text-center font-semibold text-xs md:text-sm text-black whitespace-nowrap">Actions</th>
            </tr>
          </thead>
          <tbody>
          {loading ? (
              <tr className="border-b hover:bg-gray-100 dark:hover:bg-gray-800">
                <td colSpan={5} className="py-2 px-4 text-xs md:text-sm whitespace-nowrap">
                    <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2 mt-2"></div>
                    {/* <p className="text-sm text-center text-gray-500">Loading reports...</p> */}
                </td>
              </tr>
            ) : 
            filteredData.length === 0 ? (
            <tr>
                <td
                colSpan="5"
                className="text-center dark:text-white md:text-md text-sm text-gray-500 p-2">
                    No Data Found
                </td>
            </tr>
            ) : (
                filteredData.map((req, index) => (
                  <tr key={req.id} className="border-b dark:border-gray-700 dark:hover:bg-gray-950 dark:hover:text-white dark:text-white hover:bg-gray-100 transition-colors">
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.user.name}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.request_date}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap">{req.requestable_type.split("\\").pop()}</td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 text-xs md:text-sm whitespace-nowrap capitalize">
                      <StatusBadge status={req?.status} />
                    </td>
                    <td className="border-b dark:border-gray-700 dark:text-white text-center py-2 px-4 whitespace-nowrap">
                      <div className="relative inline-block text-left">
                        <button 
                          onClick={() => setOpenDropdownId(openDropdownId === req.id ? null : req.id)}
                          className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors flex items-center justify-center mx-auto"
                        >
                          <MoreVertical size={20} />
                        </button>
                      
                        {openDropdownId === req.id && (
                          <>
                            <div 
                              className="fixed inset-0 z-10" 
                              onClick={() => setOpenDropdownId(null)}
                            ></div>
                            <div className={`absolute right-0 ${(index >= filteredData.length - 2 && index > 2) ? 'bottom-full mb-2' : 'top-full mt-2'} w-48 bg-white dark:bg-gray-800 rounded-lg shadow-2xl border dark:border-gray-700 z-20 py-1 overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150`}>
                            <button
                              onClick={() => {
                                setSelectedRequest(req);
                                setIsDetailModalOpen(true);
                                setOpenDropdownId(null);
                              }}
                              className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            >
                              <Eye size={16} className="mr-3 text-blue-500" />
                              View Details
                            </button>
                            
                            {req.status === "pending" && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setActionType("approved");
                                    setIsModalOpen(true);
                                    setOpenDropdownId(null);
                                  }}
                                  className="w-full flex items-center px-4 py-2.5 text-sm text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition-colors"
                                >
                                  <Check size={16} className="mr-3" />
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setActionType("rejected");
                                    setIsModalOpen(true);
                                    setOpenDropdownId(null);
                                  }}
                                  className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                                >
                                  <MinusCircle size={16} className="mr-3" />
                                  Reject
                                </button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isFiltering && filteredData?.length > 0 && (
        <ReactPaginate
          breakLabel="..."
          nextLabel="Next >"
          onPageChange={(event) => handlePageChange(event.selected + 1)}
          forcePage={currentPage - 1}
          pageRangeDisplayed={5}
          disabledClassName='opacity-50 cursor-not-allowed'
          disabledLinkClassName='pointer-events-none'
          pageCount={pagination.totalPages}
          previousLabel="< Prev"
          previousClassName='dark:text-white border border-gray-300 rounded-md dark:border-gray-600'
          previousLinkClassName='block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800'
          nextClassName="dark:text-white border border-gray-300 rounded-md dark:border-gray-600"
          nextLinkClassName="block w-full h-full px-3 py-1 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
          renderOnZeroPageCount={null}
          containerClassName="flex items-center justify-center mt-4 font-poppins"
          pageClassName="mx-1 border rounded dark:text-white font-poppins"
          pageLinkClassName="block w-full h-full px-3 py-1 cursor-pointer"
          activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
          activeLinkClassName="bg-blue-500 text-white dark:bg-black dark:text-white hover:bg-blue-600 dark:hover:bg-gray-900"
        />
      )}

      {/* Detail Modal */}
      {isDetailModalOpen && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Header */}
            <div className="px-6 py-4 border-b dark:border-gray-800 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
              <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                <div className="w-2 h-8 bg-blue-600 rounded-full mr-3"></div>
                Request Details
              </h2>
              <button 
                onClick={() => setIsDetailModalOpen(false)} 
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 border dark:border-gray-700/50 mb-6">
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-4">
                      <User size={20} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Employee</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedRequest.user?.name}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mr-4">
                      <Calendar size={20} className="text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Request Date</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">{selectedRequest.request_date}</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-4">
                      <Briefcase size={20} className="text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Leave Type</p>
                      <p className="text-base font-medium text-gray-900 dark:text-white">
                        {selectedRequest.requestable_type?.split("\\").pop()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mr-4">
                      <Clock size={20} className="text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">Status</p>
                      <div className="mt-0.5">
                        <StatusBadge status={selectedRequest.status} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-blue-50 dark:bg-blue-900/10 border-l-4 border-blue-500 p-4 rounded-r-lg">
                  <h3 className="text-sm font-bold text-blue-800 dark:text-blue-300 mb-2 flex items-center">
                    Reason for Leave
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    {selectedRequest.requestable?.reason || selectedRequest.reason || selectedRequest.notes || "No reason provided for this request."}
                  </p>
                </div>
                
                {selectedRequest.approver_comments && (
                  <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border dark:border-gray-700">
                    <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-2">Approver Comments</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm italic">
                      "{selectedRequest.approver_comments}"
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t dark:border-gray-800 flex justify-end gap-3">
              <button 
                onClick={() => setIsDetailModalOpen(false)}
                className="px-6 py-2.5 rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                Close
              </button>
              
              {selectedRequest.status === "pending" && (
                <>
                  <button 
                    onClick={() => {
                      setActionType("rejected");
                      setIsModalOpen(true);
                    }}
                    className="px-6 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium transition-all shadow-lg shadow-red-500/20 flex items-center"
                  >
                    <XCircle size={18} className="mr-2" />
                    Reject
                  </button>
                  <button 
                    onClick={() => {
                      setActionType("approved");
                      setIsModalOpen(true);
                    }}
                    className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium transition-all shadow-lg shadow-blue-500/20 flex items-center"
                  >
                    <CheckCircle size={18} className="mr-2" />
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* comment modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1100] p-4 animate-in fade-in duration-300">
          <div className="bg-white dark:bg-gray-900 rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in duration-300 font-poppins">
            {/* Header */}
            <div className={`px-10 py-8 flex items-center gap-6 ${
              actionType === "approved" 
                ? "bg-[#F0FDF4] dark:bg-green-900/20" 
                : "bg-[#FEF2F2] dark:bg-red-900/20"
            }`}>
              <div className={`w-16 h-16 rounded-full flex items-center justify-center border-4 ${
                actionType === "approved" 
                  ? "bg-white dark:bg-green-900/40 border-[#DCFCE7] dark:border-green-800 text-[#10B981]" 
                  : "bg-white dark:bg-red-900/40 border-[#FEE2E2] dark:border-red-800 text-[#EF4444]"
              }`}>
                {actionType === "approved" ? <CheckCircle size={32} strokeWidth={3} /> : <XCircle size={32} strokeWidth={3} />}
              </div>
              <div>
                <h3 className="text-3xl font-bold text-[#1E293B] dark:text-white leading-tight">
                  {actionType === "approved" ? "Approved Request" : "Rejected Request"}
                </h3>
                <p className="text-base text-[#64748B] dark:text-gray-400 font-medium tracking-wide">
                  {selectedRequest?.user?.name || "Employee"}
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="p-10 text-left">
              <label 
                htmlFor="approver-comments-leave" 
                className="block text-xs font-bold text-[#475569] dark:text-gray-400 uppercase tracking-[0.2em] mb-5 text-left"
              >
                MANAGER REMARK
              </label>
              <div className="relative group">
                <textarea
                  id="approver-comments-leave"
                  rows={5}
                  className="w-full p-6 text-lg text-[#1E293B] dark:text-white border-2 border-[#F1F5F9] dark:border-gray-800 dark:bg-black rounded-3xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-[#3B82F6] transition-all resize-none placeholder:text-[#94A3B8]"
                  placeholder="Write your reason or feedback here..."
                  value={approverComments}
                  onChange={(e) => setApproverComments(e.target.value)}
                />
              </div>
              <p className="mt-5 text-sm italic text-[#94A3B8] dark:text-gray-500 px-1 font-medium text-left">
                * This remark will be visible to the employee.
              </p>
            </div>

            {/* Footer */}
            <div className="px-10 py-8 bg-[#F8FAFC]/50 dark:bg-gray-950/20 border-t border-[#F1F5F9] dark:border-gray-800 flex items-center justify-end gap-10">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setApproverComments("");
                  setActionType("");
                }}
                className="text-base font-bold text-[#64748B] dark:text-gray-400 hover:text-[#0F172A] dark:hover:text-white tracking-widest transition-colors uppercase"
              >
                CANCEL
              </button>
              <button
                onClick={() => {
                  handleApproveReject(selectedRequest.id || 1, actionType);
                  setIsModalOpen(false);
                }}
                className={`px-12 py-5 rounded-2xl text-white font-bold tracking-widest transition-all shadow-xl uppercase transform hover:scale-[1.02] active:scale-[0.98] ${
                  actionType === "approved" 
                    ? "bg-[#10B981] hover:bg-[#059669] shadow-green-500/20" 
                    : "bg-[#EF4444] hover:bg-[#DC2626] shadow-red-500/20"
                }`}
              >
                {actionType === "approved" ? "APPROVED" : "REJECTED"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};


export default LeaveApproverPage;
